const ALLOWED_TAGS = new Set([
  'a', 'b', 'blockquote', 'br', 'code', 'dd', 'div', 'dl', 'dt', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 's', 'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td',
  'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
]);

const GLOBAL_ATTRIBUTES = new Set(['class', 'title', 'aria-label']);
const TAG_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
};

const SAFE_URL_PATTERN = /^(https?:|mailto:|tel:|\/|#)/i;
const SAFE_DATA_IMAGE_PATTERN = /^data:image\/(png|jpeg|jpg|gif|webp);base64,/i;

const isSafeAttribute = (tagName: string, name: string, value: string) => {
  const normalizedName = name.toLowerCase();
  if (normalizedName.startsWith('on')) return false;
  if (normalizedName === 'style') return false;
  if (!GLOBAL_ATTRIBUTES.has(normalizedName) && !TAG_ATTRIBUTES[tagName]?.has(normalizedName)) return false;

  if (normalizedName === 'href' || normalizedName === 'src') {
    const trimmed = value.trim();
    return SAFE_URL_PATTERN.test(trimmed) || (normalizedName === 'src' && SAFE_DATA_IMAGE_PATTERN.test(trimmed));
  }

  return true;
};

export const sanitizeHtml = (html?: string | null, fallback = ''): string => {
  if (!html) return fallback;
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return fallback;

  const template = document.createElement('template');
  template.innerHTML = html;

  const sanitizeNode = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    Array.from(element.attributes).forEach((attr) => {
      if (!isSafeAttribute(tagName, attr.name, attr.value)) {
        element.removeAttribute(attr.name);
      }
    });

    if (tagName === 'a' && element.getAttribute('target') === '_blank') {
      element.setAttribute('rel', 'noopener noreferrer');
    }
  };

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT);
  const nodes: Node[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach(sanitizeNode);

  return template.innerHTML;
};
